import React from 'react'
import {withRouter} from 'react-router'
import Link from '../utils/link'
import {default as i18n, lang, resolve, withPrefix, TitleDescription, textToElement} from '../utils/i18n'
import Icon from 'material-ui/Icon'
import Dialog from 'material-ui/Dialog'
import Helmet from 'react-helmet'
import '../layouts/subpage.scss'
import './timetable/timetable.scss'
import sessionizeAll from '../../data/sessionize/all.json'
import Linkify from 'react-linkify'

// ----------------------------------------------------------------
//     レイアウト/トップレベル
// ----------------------------------------------------------------

/**
 * タイムテーブルのセッションをクリックしたのかどうかを管理します。
 * この変数はセッションをクリックしたタイミングで true になり、
 * その後セッションのダイアログが閉じたタイミングで false になります。
 *
 * グローバル変数にしているのは、history の変化を超えて管理できるスコープが他に思いつかなかったためです。
 * state で管理できるのであればそちらの方が望ましいと思っています。
 */
let sessionClicked = false

export default withRouter(class Proposals extends React.Component {
  constructor (props) {
    super(props)

    this.state = { sessionId: this.findSessionId(props.location) }
  }

  /**
   * URL からダイアログ表示したいセッションの ID を取得します。
   */
  findSessionId (location) {
    const match = /session=(\d+)/.exec(location.search)
    return match ? match[1] : null
  }

  componentDidMount () {
    // history を使って ?sessionId が付与された場合、再描画がされたりはしないので
    // 手動で state を更新します。
    //
    // また、ダイアログが閉じている場合には sessionClicked を false にします。
    this.cleanupHandler = this.props.history.listen((location) => {
      const sessionId = this.findSessionId(location)
      if (!sessionId) sessionClicked = false
      this.setState({ sessionId })
    })
  }

  componentWillUnmount () {
    if (this.cleanupHandler != null) this.cleanupHandler()
  }

  onExitDialog () {
    const { history, location } = this.props
    // タイムテーブルを開きますが、ダイアログの開き方によって少しだけ動作が変わります。
    //
    // - タイムテーブルからダイアログが開かれた場合 … ブラウザの履歴を戻ります。
    // - その他の場合(URL直で開かれた場合など) … 通常の遷移をします。
    if (sessionClicked) {
      history.goBack()
    } else {
      history.push(withPrefix(resolve(location, '/timetable')))
    }
  }

  render () {
    const { location } = this.props
    const { sessionId } = this.state
    const ln = lang(location)
    const rooms1 = new Rooms(sessionizeAll.rooms)
    const rooms2 = new Rooms(sessionizeAll.rooms.filter(r => r.name !== 'Hall'))
    const categories = new Categories(sessionizeAll.categories, ln)
    const day1 = [
      ...sessionizeAll.sessions.filter(s => s.startsAt.match(/^2018-02-08T.*/)).map(toSessionInfo),
      ...SPECIAL_SESSIONS_DAY1
    ]
    const day2 = [
      ...sessionizeAll.sessions.filter(s => s.startsAt.match(/^2018-02-09T.*/)).map(toSessionInfo),
      ...SPECIAL_SESSIONS_DAY2
    ]
    const speakers = new Speakers(sessionizeAll.speakers)
    const dialogSession = (sessionId && sessionizeAll.sessions.find(s => s.id === sessionId)) || {}

    return (
      <div lang={ln} className='bg-gray2'>
        <TitleDescription texts={{
          en: {
            title: 'Timetable'
          },
          ja: {
            title: 'タイムテーブル'
          }
        }} />
        <div className='subpage-title-wrapper bg-white'>
          <h2 className='text-yellow1'><Text id='title' /></h2>
        </div>
        <div className='timetable-container'>
          <ul className='timetable-day-anchors'>
            <li><a href='#day1'>DAY.01</a></li>
            <li><a href='#day2'>DAY.02</a></li>
          </ul>
          <div className='timetable-container2 timetable-column-8'>
            <h2 id='day1' className='timetable-title' title='2018/02/08'>DAY.01 (Feb&nbsp;8th,&nbsp;2018)</h2>
            <Timetable sessions={day1} rooms={rooms1} categories={categories} speakers={speakers} />
          </div>
          <div className='timetable-container2 timetable-column-7'>
            <h2 id='day2' className='timetable-title' title='2018/02/09'>DAY.02 (Feb&nbsp;9th,&nbsp;2018)</h2>
            <Timetable sessions={day2} rooms={rooms2} categories={categories} speakers={speakers} />
          </div>
        </div>

        <SessionDetail
          sessionizeSession={dialogSession}
          rooms={rooms1}
          categories={categories}
          speakers={speakers}
          onExit={() => this.onExitDialog()}
        />
      </div>
    )
  }
})

const Text = i18n({
  en: {
    title: 'Timetable',
    video: 'Video',
    slide: 'Slide',
    error: {
      fetchProposals: 'Failed to retrieve items.'
    },
    level: {
      beginner: <span className='timetable-level-beginner'>Beginner</span>,
      expert: <span className='timetable-level-expert'>Intermediate/Expert</span>,
      niche: <span className='timetable-level-niche'>Niche Topic</span>
    },
    session: {
      welcomeTalk: 'Welcome Talk',
      party: '🎉 Party 🎉',
      lunch: '🍱 Lunch 🍱'
    }
  },
  ja: {
    title: 'タイムテーブル',
    video: '動画',
    slide: '資料',
    error: {
      fetchProposals: '一覧の取得に失敗しました。'
    },
    level: {
      beginner: <span className='timetable-level-beginner'>初心者向け</span>,
      expert: <span className='timetable-level-expert'>中・上級者向け</span>,
      niche: <span className='timetable-level-niche'>ニッチ</span>
    },
    session: {
      welcomeTalk: 'ウェルカムトーク',
      party: '🎉 パーティ 🎉',
      lunch: '🍱 ランチ 🍱'
    }
  }
})

// ----------------------------------------------------------------
//     レイアウト/タイムテーブル描画
// ----------------------------------------------------------------

class Timetable extends React.Component {
  render () {
    const { rooms, sessions } = this.props
    const data = new TimetableData(sessions, rooms)

    return (
      <table className='timetable-table'>
        <thead>
          <tr>
            <th />
            {data.rooms.names.map(n => <th key={n}>{n}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.rows.map(({ time, sessions }) =>
            <tr key={time}>
              <th>{time}</th>
              {sessions.map((c, i) => this.renderSessions(c, i))}
            </tr>
          )}
        </tbody>
      </table>
    )
  }

  /**
   * セッション情報を表示します。
   *
   * @param {Object} session セッション情報、もしくはセッションがない場合は { key: … }
   * @param {Object} [session.sessionizeSession] Sessionize 形式のセッション情報
   * @param {number} [session.span] セルの高さ
   * @param {number} [session.key] セッションがない場合にタグの key 属性を渡します
   */
  renderSessions (session, key) {
    const { sessionizeSession, special, startsAt, endsAt, span } = session || {}

    switch (special) {
      case 'welcomeTalk':
        return <TableItem
          key={key}
          lang='JA'
          span={1}
          height='short'
          title={<Text id='session.welcomeTalk' />}
          duration={toDuration(startsAt, endsAt)}
          room='Hall' />
      case 'party':
        return <TableItem
          key={key}
          span={1}
          height='long'
          title={<Text id='session.party' />}
          duration={toDuration(startsAt, endsAt)}
          room='Hall' />
      case 'lunch':
        return <td key={key} colSpan={this.props.rooms.length} className='timetable-cell-break' >
          <Text id='session.lunch' /> ({toDuration(startsAt, endsAt)}min)
        </td>
    }

    if (sessionizeSession) {
      const categories = this.props.categories.of(sessionizeSession.categoryItems)
      const room = this.props.rooms.name(sessionizeSession.roomId)
      const speakers = sessionizeSession.speakers.map(s => this.props.speakers.of(s).name)
      return <TableItem
        key={sessionizeSession.id}
        span={span}
        sessionLang={categories.lang}
        duration={toDuration(startsAt, endsAt)}
        level={categories.level}
        room={room}
        title={sessionizeSession.title}
        speaker={speakers.join(', ')}
        topic={categories.topic}
        id={sessionizeSession.id}
        link={`/timetable?session=${sessionizeSession.id}`}
        message={sessionizeSession.message}
      />
    }

    return <td key={key} className='timetable-cell timetable-sm-none' />
  }
}

// ----------------------------------------------------------------
//     レイアウト/タイムテーブルの項目
// ----------------------------------------------------------------

/**
 * セッションをテーブルに描画します
 */
const TableItem = withRouter(function TableItem ({
  history,
  location,
  id,
  title,
  speaker,
  duration,
  level,
  room,
  sessionLang,
  topic,
  span,
  height,
  link,
  message
}) {
  const heightClass = `timetable-cell-height-${height || span}`
  const content = [
    <p key='1' className='timetable-session-header'>
      {sessionLang ? <span className={`timetable-lang-${sessionLang.toLowerCase()}`}>{sessionLang.toUpperCase()}</span> : []}
      <span className='timetable-session-duration'>{room}・{duration}min</span>
    </p>,
    <h3 key='2' className='timetable-session-title'>
      {title}
    </h3>,
    <p key='3' className='timetable-session-speaker'>{speaker}</p>,
    <p key='4' className='timetable-session-categories'>
      {level}
      {' ' /* react は二つの要素の間スペースが消去されるらしい…回避策としてスペースをあえて付与 */}
      {topic ? <span className='timetable-category'>{topic}</span> : []}
    </p>
  ]
  if (message) {
    const msg = message[lang(location)]
    if (msg) {
      content.push(<p className='timetable-session-message'>{textToElement(msg, true)}</p>)
    }
  }
  const onClick = (e) => {
    sessionClicked = true

    // <Link> を使うとなぜか1秒くらい待たされる現象が起きています。
    // 回避策として、手動で遷移を行います。
    // (/timetable/?session=~ の URL は Gatsby で生成していない URL だからかもしれません)
    //
    // なお React ではクリックイベントのハンドラで false を返してもイベントが伝搬して
    // しまうようです。対策として preventDefault() などを手動で呼び出しています。
    // 参考: https://reactjs.org/docs/handling-events.html
    history.push(withPrefix(resolve(location, link)))
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <td className={`timetable-cell ${heightClass}`} rowSpan={span}>
      <div className='timetable-session-card' to={link} >
        {link
          ? <Link to={link} className='timetable-session-link'><div onClick={onClick}>{content}</div></Link>
          : content}
      </div>
    </td>
  )
})

// ----------------------------------------------------------------
//     レイアウト/ダイアログ
// ----------------------------------------------------------------

const SessionDetail = withRouter(function ({
  location,
  sessionizeSession,
  categories,
  rooms,
  speakers,
  onExit
}) {
  if (!sessionizeSession.id) return <Dialog />

  const category = categories.of(sessionizeSession.categoryItems)
  const room = rooms.name(sessionizeSession.roomId)
  const title = sessionizeSession.title
  const time = timeToLongTextFormat(sessionizeSession.startsAt, sessionizeSession.endsAt)
  const speakersDetail = sessionizeSession.speakers.map(s => speakers.of(s))
  const msg = sessionizeSession.message ? sessionizeSession.message[lang(location)] : null
  const videoUrl = sessionizeSession.videoUrl
  const slideUrl = sessionizeSession.slideUrl

  return <Dialog open maxWidth='md' onBackdropClick={onExit} >
    <Helmet title={`${title} | DroidKaigi 2018`} />
    <div className='timetable-dialog'>
      <div style={{ marginTop: '32px', marginBottom: '32px' }}>
        <h2 className='timetable-dialog-title'>{title}</h2>
        <p className='timetable-dialog-subtitle' >
          <span className={`timetable-dialog-lang-${category.lang}`}>{category.lang.toUpperCase()}</span>
          {' ' /* react は二つの要素の間スペースが消去されるらしい…回避策としてスペースをあえて付与 */}
          <span>{room}</span>
          {' － '}<span>{time}</span>
        </p>
        <p className='timetable-dialog-categories'>
          {category.level}
          <span className='timetable-category'>{category.topic}</span>
        </p>
        {(() => {
          if (videoUrl || slideUrl) {
            return <p className='timetable-dialog-categories text-gray'>
              <Icon className='timetable-link-icon'>link</Icon>
              {videoUrl ? <a className='timetable-doc-link' href={videoUrl} target='_blank'><Icon className='timetable-icon'>local_movies</Icon><Text id='video' /></a> : null}
              {slideUrl ? <a className='timetable-doc-link' href={slideUrl} target='_blank'><Icon className='timetable-icon'>insert_drive_file</Icon><Text id='slide' /></a> : null}
            </p>
          }
        })()}
        {msg ? <p className='timetable-dialog-message timetable-session-message'>{textToElement(msg, true)}</p> : null}
        <p className='timetable-dialog-body'><AutoLink text={sessionizeSession.description} /></p>

        {speakersDetail.map((s, i) =>
          <div key={i} className='timetable-dialog-speaker-top'>
            <div className='timetable-dialog-speaker-imagebox'>
              {s.profilePicture ? <img style={{ width: '100%' }} src={s.profilePicture} /> : []}
            </div>
            <div>
              <h3 className='timetable-dialog-top-content'>{s.name}</h3>
              <p className='timetable-dialog-tagline'>{s.tagLine}</p>
              <p className='timetable-dialog-paragraph text-gray'>
                {s.links.length ? <Icon className='timetable-link-icon'>link</Icon> : []}
                {s.links.map(l =>
                  <a key={l.url} target='_blank' href={l.url} className='timetable-link-text'>
                    {l.title}
                  </a>
                )}
              </p>
              <p className='timetable-dialog-body'><AutoLink text={s.bio} /></p>
            </div>
          </div>
        )}
      </div>
    </div>
  </Dialog>
})

function AutoLink ({text}) {
  return <Linkify properties={{target: '_blank'}}>{text}</Linkify>
}

function timeToLongTextFormat (start, end) {
  const s = /(\d+)-(\d+)-(\d+)T(\d+):(\d+)/.exec(start)
  const e = /(\d+)-(\d+)-(\d+)T(\d+):(\d+)/.exec(end)
  return `${s[1]}/${s[2]}/${s[3]} ${s[4]}:${s[5]}-${e[4]}:${e[5]}`
}

// ----------------------------------------------------------------
//     各種表示データ/低レベル情報(部屋、スピーカー、カテゴリ)
// ----------------------------------------------------------------

const ROOM_ID_HALL = 513
const ROOM_ID_SPONSORS = 521

/**
 * 使用する部屋の集合を管理します。
 *
 * ## データ形式について
 * 
 * `this.values` には sessionize で提供された形式でデータが入っています。
 * このクラスではこの形式を隠蔽し、各メソッドは適宜加工された表現を返します。
 */
class Rooms {
  constructor (sessionizeRooms) {
    // Sessionize の部屋の一覧は不要なスポンサールームも含まれているので除外します。
    this.values = sessionizeRooms
      .filter(r => r.id !== ROOM_ID_SPONSORS)
      .sort((r1, r2) => r1.sort - r2.sort)
  }

  get length () {
    return this.values.length
  }

  get names () {
    // Sessionize のデータは Room 1 A+B のような名前になっているので、A+B を除去
    // 命名ルールが変わるとこちら側の変更も必須です
    return this.values.map(r => r.name.replace(/(Room \d+).*/, '$1'))
  }

  name (id) {
    return this.names[this.indexOf(id)]
  }

  room (id) {
    return this.values.find(r => r.id === id)
  }

  indexOf (id) {
    return this.values.findIndex(r => r.id === id)
  }
}

/**
 * スピーカー情報の集合を管理します。
 */
class Speakers {
  constructor (sessionizeSpeakers) {
    const values = {}
    for (const s of sessionizeSpeakers) {
      values[s.id] = {
        name: s.fullName,
        tagLine: s.tagLine,
        bio: s.bio,
        profilePicture: s.profilePicture,
        links: s.links
      }
    }

    this.values = values
  }

  of (id) {
    return this.values[id]
  }
}

/**
 * 時間情報の集合を管理します。
 * 
 * ## データ形式について
 * 
 * `this.values` には `2018-02-08T10:00:00` のような形式でデータが入っています。
 * このクラスではこの形式を隠蔽し、各メソッドは適宜加工された表現を返します。
 */
class TimeBlocks {
  constructor (times) {
    const timeSet = {}
    for (const s of times) {
      timeSet[s] = true
    }

    this.values = Object.keys(timeSet).sort()
  }

  get times () {
    return this.values.map(t => t.replace(/^.*T(\d\d:\d\d):.*/, '$1'))
  }

  indexOf (time) {
    return this.values.findIndex(t => t === time)
  }

  indexAfter (time) {
    const res = this.values.findIndex(t => t >= time)
    return res === -1 ? this.values.length : res
  }
}

function toDuration (startsAt, endsAt) {
  return (new Date(endsAt).getTime() - new Date(startsAt).getTime()) / (60 * 1000)
}

/**
 * カテゴリ情報の集合を管理します。
 *
 * ## データ形式について
 * 
 * sessionize はカテゴリの種類(Language など)と各カテゴリ(Englishなど)
 * の入れ子構造でデータを渡します。
 * 
 * 一方、このクラスは以下のようにタイムライン表示に最適化した形に加工したカテゴリ情報を提供します
 * (`of` メソッド参照)。
 * 
 * ```
 * {
 *   lang: 'ja',
 *   duration: '50',
 *   topic: '…',
 *   level: 'niche'
 * }
 * ```
 *
 * 内部表現は { category: '(カテゴリの種類)', value: '各カテゴリの値' } の配列となっています。
 */
class Categories {
  constructor (sessionizeCategories, lang) {
    const items = {}
    for (const c of sessionizeCategories) {
      for (const { id, name } of c.items) {
        switch (c.title) {
          case 'Language':
            const langId = id === 3482 ? 'en' : id === 3483 ? 'ja' : 'mixed'
            items[id] = { category: 'lang', value: langId }
            break
          case 'Session format':
            const minutes = name.replace(/minutes$/, '')
            items[id] = { category: 'duration', value: minutes }
            break
          case 'トピック / Topic':
            items[id] = { category: 'topic', value: this.parseJaAndEn(name, lang) }
            break
          case 'Level':
            const levelId = id === 3542 ? 'niche' : id === 3541 ? 'expert' : 'beginner'
            items[id] = { category: 'level', value: <Text id={`level.${levelId}`} /> }
            break
        }
      }
    }
    this.items = items
  }

  /**
   * Sessionize データ形式(`(日本語) / (英語)`) から現在の言語のテキストを抽出します。
   */
  parseJaAndEn (jaAndEn, lang) {
    const match = /(.+) \/ (.+)/.exec(jaAndEn)
    if (match) {
      return lang === 'ja' ? match[1] : match[2]
    } else {
      return jaAndEn
    }
  }

  of (categoryIds) {
    const res = {}
    for (const i of categoryIds) {
      const { category, value } = this.items[i]
      res[category] = value
    }
    return res
  }
}

// ----------------------------------------------------------------
//     各種表示データ/特別セッション
// ----------------------------------------------------------------

//
// ### セッション情報の形式
//
// {
//   startsAt: '(日付形式)',
//   endsAt: '(日付形式)',
//   roomId: '(Sessionize で提供している部屋の ID、部屋がない場合は undefined)',
//   special: '特別セッションを識別するための文字列。講演・ハンズオンの場合は undefined',
//   sessionizeSession: 'Sessionize から受け取った講演情報。特別セッションの場合は undefined'
// }
//

/**
 * 1日目の特別セッション
 */
const SPECIAL_SESSIONS_DAY1 = [
  { special: 'welcomeTalk', startsAt: '2018-02-08T10:00:00', endsAt: '2018-02-08T10:20:00', roomId: ROOM_ID_HALL },
  { special: 'lunch', startsAt: '2018-02-08T11:50:00', endsAt: '2018-02-08T12:50:00' },
  { special: 'party', startsAt: '2018-02-08T17:40:00', endsAt: '2018-02-08T19:40:00', roomId: ROOM_ID_HALL }
]

/**
 * 2日目の特別セッション
 */
const SPECIAL_SESSIONS_DAY2 = [
  { special: 'lunch', startsAt: '2018-02-09T11:50:00', endsAt: '2018-02-09T12:50:00' }
]

/**
 * Sessionize 形式の講演・ハンズオン情報を、本モジュールで使う形式でラップします。
 */
function toSessionInfo (sessionizeSession) {
  return {
    startsAt: sessionizeSession.startsAt,
    endsAt: sessionizeSession.endsAt,
    roomId: sessionizeSession.roomId,
    sessionizeSession
  }
}

// ----------------------------------------------------------------
//     各種表示データ/タイムテーブル用データ
// ----------------------------------------------------------------

/**
 * タイムテーブル表示で、特に要素を割り当てる必要が無いことを表します。
 */
const ALREADY_FILLED = 'ALREADY_FILLED'

/**
 * タイムテーブル表示に最適化した形でグループ化されたセッション情報です。
 */
class TimetableData {
  constructor (sessions, rooms) {
    const timeBlocks = new TimeBlocks(sessions.map(s => s.startsAt))
    const res = timeBlocks.times.map(() => new Array(rooms.length))
    for (const session of sessions) {
      const room = rooms.indexOf(session.roomId)
      const start = timeBlocks.indexOf(session.startsAt)
      const span = timeBlocks.indexAfter(session.endsAt) - start

      res[start][room] = { ...session, span }
      if (room === -1) {
        // 全ての部屋に流れるもの
        res[start][0] = { ...session, span }
        for (let i = 1; i < rooms.length; ++i) {
          res[start][i] = ALREADY_FILLED
        }
      } else {
        // 特定の部屋に流れるもの
        for (let i = 1; i < span; ++i) {
          res[start + i][room] = ALREADY_FILLED
        }
      }
    }

    this.rooms = rooms
    this.timeBlocks = timeBlocks
    this.cells = res
  }

  get rows () {
    return this.timeBlocks.times.map((time, i) => {
      const r = this.cells[i]
      const sessions = this.rooms.names
        .map((_, i) => r[i])
        .filter(c => c !== ALREADY_FILLED)

      return { time, sessions }
    })
  }
}



// WEBPACK FOOTER //
// ./src/pages/timetable.js